/**
 * Global Search Component
 * Provides search functionality across organizations, calculations, and reports
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  ChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useOrganizationService } from '@/hooks/useOrganizationService';
import { useCalculationService } from '@/hooks/useCalculationService';
import { useReportManagementService } from '@/hooks/useReportManagementService';
import type { Organization } from '@/types/organization';
import type { CalculationInput } from '@/types/calculation';
import type { SavedReport } from '@/types/reports';

interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'organization' | 'calculation' | 'report';
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface GlobalSearchProps {
  className?: string;
}

export const GlobalSearch = ({ className = '' }: GlobalSearchProps) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { organizations } = useOrganizationService();
  const { calculations } = useCalculationService();
  const { reports } = useReportManagementService();

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }

      // Escape to close
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search function
  const performSearch = useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      const searchResults: SearchResult[] = [];
      const lowerQuery = searchQuery.toLowerCase();

      // Search organizations
      organizations.forEach((org: Organization) => {
        if (
          org.name.toLowerCase().includes(lowerQuery) ||
          org.description?.toLowerCase().includes(lowerQuery)
        ) {
          searchResults.push({
            id: org.id,
            title: org.name,
            subtitle: org.description || 'Organización',
            type: 'organization',
            href: `/organizations/${org.id}`,
            icon: BuildingOfficeIcon,
          });
        }
      });

      // Search calculations
      calculations.forEach((calc: CalculationInput) => {
        if (
          calc.organizationName?.toLowerCase().includes(lowerQuery) ||
          calc.notes?.toLowerCase().includes(lowerQuery)
        ) {
          searchResults.push({
            id: calc.id!,
            title: `Cálculo - ${calc.organizationName}`,
            subtitle: `${calc.totalResidents} residentes - ${new Date(calc.createdAt!).toLocaleDateString()}`,
            type: 'calculation',
            href: `/calculations/${calc.id}`,
            icon: CalculatorIcon,
          });
        }
      });

      // Search reports
      reports.forEach((report: SavedReport) => {
        if (
          report.title.toLowerCase().includes(lowerQuery) ||
          report.description?.toLowerCase().includes(lowerQuery) ||
          report.organizationName?.toLowerCase().includes(lowerQuery)
        ) {
          searchResults.push({
            id: report.id,
            title: report.title,
            subtitle: `${report.organizationName} - ${new Date(report.createdAt).toLocaleDateString()}`,
            type: 'report',
            href: `/reports/${report.id}`,
            icon: ChartBarIcon,
          });
        }
      });

      // Sort results by relevance (exact matches first, then partial matches)
      searchResults.sort((a, b) => {
        const aExact = a.title.toLowerCase().includes(lowerQuery);
        const bExact = b.title.toLowerCase().includes(lowerQuery);

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return a.title.localeCompare(b.title);
      });

      setResults(searchResults.slice(0, 10)); // Limit to 10 results
      setIsLoading(false);
    },
    [organizations, calculations, reports]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query && isOpen) {
        performSearch(query);
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, isOpen, performSearch]);

  const handleResultClick = (result: SearchResult) => {
    navigate(result.href);
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (query) {
      performSearch(query);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={handleInputFocus}
          placeholder="Buscar (Ctrl+K)"
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
        />
        {query && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Search Results */}
      {isOpen && (query || isLoading) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              Buscando...
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map(result => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <result.icon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {result.subtitle}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {result.type === 'organization' && 'Org'}
                        {result.type === 'calculation' && 'Calc'}
                        {result.type === 'report' && 'Rep'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : query && !isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No se encontraron resultados para "{query}"</p>
              <p className="text-xs mt-1">
                Intenta con otros términos de búsqueda
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
